// admin/src/pages/Analytics.jsx - USING CSS CLASSES
import React, { useEffect, useState, useRef, useCallback } from 'react';
import TopNav from '../components/TopNav';
import { useAdminAuth } from '../auth/AdminAuthContext';

const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Analytics() {
  const { secret } = useAdminAuth();
  const [analytics, setAnalytics] = useState({
    totalSessions: 0,
    completedSessions: 0,
    avgResponses: 0,
    completionRate: 0,
    topMindsets: [],
    topAreas: [],
    topPrograms: [],
    degreeDistribution: [],
    ageDistribution: []
  });
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('all');
  
  const didFetchRef = useRef(false);

  const loadAnalyticsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tenantId: 'demo-university' });
      
      if (dateRange !== 'all') {
        const now = new Date();
        const daysAgo = {
          '7days': 7,
          '30days': 30,
          '90days': 90
        }[dateRange];
        
        const after = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
        params.append('after', after.toISOString());
      }

      const [analyticsRes, programsRes] = await Promise.all([
        fetch(`${API_ROOT}/api/analytics/summary?${params.toString()}`, {
          headers: { 'x-admin-secret': secret || '' },
        }),
        fetch(`${API_ROOT}/api/programs?tenantId=demo-university`, {
          headers: { 'x-admin-secret': secret || '' },
        })
      ]);

      if (analyticsRes && analyticsRes.ok) {
        const data = await analyticsRes.json();
        const stats = data.stats || {};
        const completionRate = stats.totalSessions > 0 
          ? Math.round((stats.completedSessions / stats.totalSessions) * 100) 
          : 0;

        setAnalytics({
          totalSessions: stats.totalSessions || 0,
          completedSessions: stats.completedSessions || 0,
          avgResponses: Math.round(stats.avgResponses || 0),
          completionRate,
          topMindsets: (data.topMindsets || []).map(m => ({ tag: m._id, count: m.count })),
          topAreas: (data.topAreas || []).map(a => ({ tag: a._id, count: a.count })),
          topPrograms: (data.topPrograms || []).map(p => ({ id: p._id, count: p.count })),
          degreeDistribution: (data.degreeDistribution || []).map(d => ({ level: d._id, count: d.count })),
          ageDistribution: (data.ageDistribution || []).map(a => ({ range: a._id, count: a.count }))
        });
      } else if (analyticsRes) {
        const text = await analyticsRes.text().catch(() => '');
        throw new Error(`Analytics fetch failed: ${analyticsRes.status} ${analyticsRes.statusText} ${text}`);
      }

      if (programsRes && programsRes.ok) {
        const data = await programsRes.json();
        setPrograms(data);
      } else if (programsRes && programsRes.status !== 304) {
        console.warn('Programs fetch non-ok:', programsRes.status);
        setPrograms([]);
      }
    } catch (err) {
      console.error('Failed to load analytics data:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [secret, dateRange]);

  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  useEffect(() => {
    if (didFetchRef.current) {
      loadAnalyticsData();
    }
  }, [dateRange, loadAnalyticsData]);

  const topProgramsWithTitles = analytics.topPrograms.map(tp => {
    const program = programs.find(p => String(p._id) === String(tp.id));
    return { 
      ...tp, 
      title: program?.title || tp.id || 'Unknown Program', 
      program 
    };
  });

  const formatTag = (slug) => {
    if (!slug) return '';
    return slug.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const formatDegreeLevel = (slug) => {
    const map = {
      bachelors: "Bachelor's",
      masters: "Master's",
      phd: "Ph.D.",
      associate: "Associate's",
      certificate: "Certificate",
      other: "Other"
    };
    return map[slug] || slug;
  };

  const formatAgeRange = (slug) => {
    const map = {
      'under-18': 'Under 18',
      '18-24': '18-24',
      '25-34': '25-34',
      '35-44': '35-44',
      '45-54': '45-54',
      '55+': '55+'
    };
    return map[slug] || slug;
  };

  const getBarColor = (index) => {
    const colors = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      'linear-gradient(135deg, #84cc16 0%, #65a30d 100%)',
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="admin-container">
      <TopNav />
      <div className="page-pad">
        <header className="adm-header">
          <div>
            <h1 className="adm-heading">📊 Analytics Dashboard</h1>
            <p className="adm-subheading">Visual insights and trends from quiz sessions</p>
          </div>
        </header>

        {/* Controls */}
        <div className="card" style={{ marginBottom: 32, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="muted" style={{ fontWeight: 600 }}>📅 Time Period:</span>
          {['all', '7days', '30days', '90days'].map(range => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={dateRange === range ? 'btn btn--primary' : 'btn'}
            >
              {range === 'all' ? '🌐 All Time' : range === '7days' ? '📆 Last 7 Days' : range === '30days' ? '📅 Last 30 Days' : '📊 Last 90 Days'}
            </button>
          ))}
          <button onClick={loadAnalyticsData} className="btn" style={{ marginLeft: 'auto' }}>
            🔄 Refresh
          </button>
        </div>

        {loading && (
          <div className="adm-empty" style={{ padding: 60 }}>
            <div style={{ fontSize: '3rem', marginBottom: 16 }}>⏳</div>
            <div>Loading analytics...</div>
          </div>
        )}
        
        {error && (
          <div className="card" style={{ 
            padding: 20, 
            background: '#fef2f2', 
            border: '2px solid #ef4444', 
            color: '#991b1b',
            marginBottom: 24
          }}>
            ⚠️ {error}
          </div>
        )}

        {!loading && (
          <>
            {/* Hero Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 20,
              marginBottom: 32
            }}>
              <div className="card" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{ position: 'absolute', top: -20, right: -20, fontSize: '6rem', opacity: 0.15 }}>📊</div>
                <div style={{ fontSize: '3.5rem', fontWeight: 800, marginBottom: 8, position: 'relative' }}>
                  {analytics.totalSessions}
                </div>
                <div style={{ fontSize: '1rem', opacity: 0.95, fontWeight: 600 }}>Total Sessions</div>
              </div>

              <div className="card" style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{ position: 'absolute', top: -20, right: -20, fontSize: '6rem', opacity: 0.15 }}>✅</div>
                <div style={{ fontSize: '3.5rem', fontWeight: 800, marginBottom: 8, position: 'relative' }}>
                  {analytics.completedSessions}
                </div>
                <div style={{ fontSize: '1rem', opacity: 0.95, fontWeight: 600 }}>Completed</div>
                <div style={{ fontSize: '0.875rem', opacity: 0.85, marginTop: 4 }}>
                  {analytics.completionRate}% completion rate
                </div>
              </div>

              <div className="card" style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                boxShadow: '0 10px 40px rgba(245, 158, 11, 0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{ position: 'absolute', top: -20, right: -20, fontSize: '6rem', opacity: 0.15 }}>💬</div>
                <div style={{ fontSize: '3.5rem', fontWeight: 800, marginBottom: 8, position: 'relative' }}>
                  {analytics.avgResponses}
                </div>
                <div style={{ fontSize: '1rem', opacity: 0.95, fontWeight: 600 }}>Avg Responses</div>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="admin-grid">
              {/* Top Mindsets */}
              <div className="adm-card">
                <div className="adm-card__body">
                  <h2 className="adm-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span>🧠</span> Top Primary Mindsets
                  </h2>
                  <div className="adm-section">
                    {analytics.topMindsets.map((item, idx) => {
                      const percentage = analytics.totalSessions > 0 ? (item.count / analytics.totalSessions) * 100 : 0;
                      return (
                        <div key={idx}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span className="adm-kvp__k">{formatTag(item.tag)}</span>
                            <span style={{ fontWeight: 700, color: '#0ea5e9' }}>
                              {item.count} ({Math.round(percentage)}%)
                            </span>
                          </div>
                          <div style={{
                            height: 32,
                            background: '#f1f5f9',
                            borderRadius: 8,
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              height: '100%',
                              width: `${percentage}%`,
                              background: getBarColor(idx),
                              transition: 'width 0.6s ease'
                            }} />
                          </div>
                        </div>
                      );
                    })}
                    {analytics.topMindsets.length === 0 && (
                      <div className="adm-empty">No mindset data yet</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Top Areas - Bubbles */}
              <div className="adm-card">
                <div className="adm-card__body">
                  <h2 className="adm-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span>🎯</span> Top Primary Areas
                  </h2>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, minHeight: 200, alignContent: 'flex-start' }}>
                    {analytics.topAreas.map((item, idx) => {
                      const maxCount = analytics.topAreas[0]?.count || 1;
                      const size = 60 + ((item.count / maxCount) * 80);
                      const percentage = analytics.totalSessions > 0 ? Math.round((item.count / analytics.totalSessions) * 100) : 0;
                      return (
                        <div
                          key={idx}
                          style={{
                            width: size,
                            height: size,
                            borderRadius: '50%',
                            background: getBarColor(idx),
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            textAlign: 'center',
                            padding: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            transition: 'transform 0.3s',
                            cursor: 'default'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          title={`${formatTag(item.tag)}: ${item.count} students (${percentage}%)`}
                        >
                          <div style={{ fontSize: '1.2rem', marginBottom: 2 }}>{item.count}</div>
                          <div style={{ fontSize: '0.65rem', lineHeight: 1.2, opacity: 0.95, wordBreak: 'break-word' }}>
                            {formatTag(item.tag).split(' ').slice(0, 2).join(' ')}
                          </div>
                        </div>
                      );
                    })}
                    {analytics.topAreas.length === 0 && (
                      <div className="adm-empty" style={{ width: '100%' }}>No area data yet</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Top Programs */}
              <div className="adm-card">
                <div className="adm-card__body">
                  <h2 className="adm-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span>🎓</span> Most Recommended Programs
                  </h2>
                  <div className="adm-section">
                    {topProgramsWithTitles.map((item, idx) => {
                      const percentage = analytics.totalSessions > 0 ? Math.round((item.count / analytics.totalSessions) * 100) : 0;
                      return (
                        <div key={idx} className="adm-kvp" style={{
                          padding: '16px 20px',
                          background: idx === 0 ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' : 
                                      idx === 1 ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' :
                                      idx === 2 ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : '#f8fafc',
                          borderRadius: 12,
                          border: idx < 3 ? `2px solid ${idx === 0 ? '#10b981' : idx === 1 ? '#3b82f6' : '#f59e0b'}` : '1px solid #e2e8f0',
                          transition: 'transform 0.2s',
                          cursor: 'default',
                          alignItems: 'center'
                        }}>
                          <div style={{
                            fontSize: idx < 3 ? '2rem' : '1.5rem',
                            fontWeight: 800,
                            color: idx === 0 ? '#10b981' : idx === 1 ? '#3b82f6' : idx === 2 ? '#f59e0b' : '#94a3b8',
                            minWidth: 48,
                            textAlign: 'center'
                          }}>
                            {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                          </div>
                          <div className="adm-kvp__v" style={{ fontWeight: 600 }}>{item.title}</div>
                          <div className="adm-badge adm-badge--success">
                            {item.count} ({percentage}%)
                          </div>
                        </div>
                      );
                    })}
                    {topProgramsWithTitles.length === 0 && (
                      <div className="adm-empty">No program data yet</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Degree Distribution */}
              {analytics.degreeDistribution.length > 0 && (
                <div className="adm-card">
                  <div className="adm-card__body">
                    <h2 className="adm-title">🎓 Degree Level Distribution</h2>
                    <div className="adm-section">
                      {analytics.degreeDistribution.map((item, idx) => {
                        const percentage = (item.count / analytics.totalSessions) * 100;
                        return (
                          <div key={idx}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                              <span className="adm-kvp__k">{formatDegreeLevel(item.level)}</span>
                              <span className="muted">{item.count} ({Math.round(percentage)}%)</span>
                            </div>
                            <div style={{ height: 24, background: '#f1f5f9', borderRadius: 8, overflow: 'hidden' }}>
                              <div style={{
                                height: '100%',
                                width: `${percentage}%`,
                                background: '#3b82f6',
                                transition: 'width 0.6s ease'
                              }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Age Distribution */}
              {analytics.ageDistribution.length > 0 && (
                <div className="adm-card">
                  <div className="adm-card__body">
                    <h2 className="adm-title">👥 Age Range Distribution</h2>
                    <div className="adm-section">
                      {analytics.ageDistribution.map((item, idx) => {
                        const percentage = (item.count / analytics.totalSessions) * 100;
                        return (
                          <div key={idx}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                              <span className="adm-kvp__k">{formatAgeRange(item.range)}</span>
                              <span className="muted">{item.count} ({Math.round(percentage)}%)</span>
                            </div>
                            <div style={{ height: 24, background: '#f1f5f9', borderRadius: 8, overflow: 'hidden' }}>
                              <div style={{
                                height: '100%',
                                width: `${percentage}%`,
                                background: '#10b981',
                                transition: 'width 0.6s ease'
                              }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}